import React, { useState, useEffect, useCallback } from 'react';
import cn from 'classnames';

import { getTranslation } from 'helpers/getTranslation';

import { Drawer } from 'components/shared/Drawer';
import { Button } from 'components/shared/Button';
import { Radio } from 'components/shared/Radio';
import { Typography } from 'components/shared/Typography';
import { Loading } from 'components/shared/Loading';
import { Icon } from 'components/shared/Icon';

import { CoursePreview } from 'components/CoursePreview';
import { SchoolPreview } from 'components/SchoolPreview';
import { Tooltip } from 'components/Tooltip';

import type { LinkedCourse } from 'store/courses-lessons/helpers';

import type {
  CourseLessonModuleGroup,
  CourseLessonOption,
} from './LessonReplaceInCourseDrawer.helpers';

import styles from './LessonReplaceInCourseDrawer.module.scss';

type CourseWithTypeLabel = LinkedCourse & {
  typeLabel?: string;
};

const LessonReplaceInCourseDrawer = ({
  open,
  lessonTitle,
  loading,
  lessonsLoading,
  saving,
  canSave,
  errorMessage,
  courses,
  courseLessons,
  isModularLessons,
  lessonModuleGroups,
  selectedCourseId,
  selectedReplacedLessonId,
  onClose,
  onCancel,
  onSave,
  onCourseSelect,
  onReplacedLessonSelect,
}: {
  open: boolean;
  lessonTitle: string;
  loading: boolean;
  lessonsLoading: boolean;
  saving: boolean;
  canSave: boolean;
  errorMessage: string | null;
  courses: CourseWithTypeLabel[];
  courseLessons: CourseLessonOption[];
  isModularLessons: boolean;
  lessonModuleGroups: CourseLessonModuleGroup[] | null;
  selectedCourseId: string | null;
  selectedReplacedLessonId: string | null;
  onClose: () => void;
  onCancel: () => void;
  onSave: () => void;
  onCourseSelect: (courseId: string) => void;
  onReplacedLessonSelect: (lessonId: string) => void;
}) => {
  const isInteractionDisabled = loading || saving;
  const [collapsedModuleIds, setCollapsedModuleIds] = useState(
    () => new Set<string>(),
  );

  useEffect(() => {
    setCollapsedModuleIds(new Set());
  }, [selectedCourseId]);

  const handleModuleHeaderClick = useCallback((moduleGroupId: string) => {
    setCollapsedModuleIds((previousCollapsedModuleIds) => {
      const nextCollapsedModuleIds = new Set(previousCollapsedModuleIds);

      if (nextCollapsedModuleIds.has(moduleGroupId)) {
        nextCollapsedModuleIds.delete(moduleGroupId);
      } else {
        nextCollapsedModuleIds.add(moduleGroupId);
      }

      return nextCollapsedModuleIds;
    });
  }, []);

  const renderLessonItem = (courseLesson: CourseLessonOption) => {
    const isSelected = selectedReplacedLessonId === courseLesson.id;
    const lessonLabel = courseLesson.title;

    return (
      <div
        key={courseLesson.id}
        className={cn(styles.lessonItem, {
          [styles.itemDisabled]: isInteractionDisabled,
          [styles.lessonItemSelected]: isSelected,
        })}
        onClick={() => {
          if (!isInteractionDisabled) {
            onReplacedLessonSelect(courseLesson.id);
          }
        }}
      >
        <Radio
          className={styles.radio}
          name="replace-lesson"
          checked={isSelected}
          disabled={isInteractionDisabled}
          readOnly
          onChange={() => onReplacedLessonSelect(courseLesson.id)}
        />
        <Typography
          className={styles.lessonTitle}
          variant="body3"
          mode="compact"
          title={lessonLabel || undefined}
        >
          {lessonLabel || getTranslation('common.text.untitled')}
        </Typography>
      </div>
    );
  };

  const hasReplaceableLessons = isModularLessons
    ? Boolean(lessonModuleGroups?.length)
    : Boolean(courseLessons.length);

  return (
    <Drawer
      open={open}
      headerClassName={styles.drawerHeader}
      headerContent={getTranslation(
        'courses_lessons_page.replace_drawer.header',
      )}
      onClose={onClose}
    >
      <div className={styles.body}>
        <div className={styles.content}>
          <Typography
            className={styles.lessonSubtitle}
            variant="body3"
            mode="compact"
            title={lessonTitle}
          >
            {getTranslation('courses_lessons_page.replace_drawer.lesson_label', {
              lessonTitle,
            })}
          </Typography>

          <Typography className={styles.warning} variant="body3" mode="compact">
            {getTranslation('courses_lessons_page.replace_drawer.warning')}
          </Typography>

          {errorMessage ? (
            <Typography className={styles.error} variant="body3" mode="compact">
              {errorMessage}
            </Typography>
          ) : null}

          {loading ? (
            <div className={styles.loading}>
              <Loading />
            </div>
          ) : courses.length ? (
            <>
              <Typography
                className={styles.sectionLabel}
                variant="body3"
                weight="medium"
                mode="compact"
              >
                {getTranslation(
                  'courses_lessons_page.replace_drawer.course_label',
                )}
              </Typography>

              <div className={styles.list}>
                {courses.map((course) => {
                  const isSelected = selectedCourseId === course.id;

                  return (
                    <div
                      key={course.id}
                      className={cn(styles.item, {
                        [styles.itemDisabled]: isInteractionDisabled,
                        [styles.itemSelected]: isSelected,
                      })}
                      onClick={() => {
                        if (!isInteractionDisabled) {
                          onCourseSelect(course.id);
                        }
                      }}
                    >
                      <Radio
                        className={styles.radio}
                        name="replace-course"
                        checked={isSelected}
                        disabled={isInteractionDisabled}
                        readOnly
                        onChange={() => onCourseSelect(course.id)}
                      />
                      <CoursePreview
                        className={styles.coursePreview}
                        preview={course.preview}
                      />
                      <div className={styles.courseInfo}>
                        <Typography
                          className={styles.courseTitle}
                          variant="body3"
                          weight="medium"
                          mode="compact"
                          title={course.title || undefined}
                        >
                          {course.title ||
                            getTranslation('common.text.untitled')}
                        </Typography>
                        {course.typeLabel ? (
                          <Typography
                            className={styles.courseType}
                            variant="body3"
                            mode="compact"
                          >
                            {course.typeLabel}
                          </Typography>
                        ) : null}
                      </div>
                      {course.school ? (
                        <Tooltip
                          text={course.school.name}
                          triggerClassName={styles.schoolTrigger}
                          triggerElement={
                            <SchoolPreview
                              className={styles.schoolPreview}
                              preview={course.school.preview}
                              name={course.school.name}
                            />
                          }
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>

              {selectedCourseId ? (
                <>
                  <Typography
                    className={styles.sectionLabel}
                    variant="body3"
                    weight="medium"
                    mode="compact"
                  >
                    {getTranslation(
                      'courses_lessons_page.replace_drawer.replaced_lesson_label',
                    )}
                  </Typography>

                  {lessonsLoading ? (
                    <div className={styles.loading}>
                      <Loading />
                    </div>
                  ) : hasReplaceableLessons ? (
                    <div className={styles.lessonList}>
                      {isModularLessons && lessonModuleGroups?.length
                        ? lessonModuleGroups.map((moduleGroup) => {
                            const expanded = !collapsedModuleIds.has(
                              moduleGroup.id,
                            );

                            return (
                              <div
                                key={moduleGroup.id}
                                className={styles.moduleGroup}
                              >
                                <div
                                  className={styles.moduleHeader}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() =>
                                    handleModuleHeaderClick(moduleGroup.id)
                                  }
                                  onKeyDown={(event) => {
                                    if (
                                      event.key === 'Enter' ||
                                      event.key === ' '
                                    ) {
                                      event.preventDefault();
                                      handleModuleHeaderClick(moduleGroup.id);
                                    }
                                  }}
                                >
                                  <Icon
                                    className={cn(styles.moduleChevron, {
                                      [styles.moduleChevronExpanded]: expanded,
                                    })}
                                    name="chevronDown"
                                  />
                                  <Typography
                                    className={styles.moduleTitle}
                                    variant="body2"
                                    weight="medium"
                                    mode="compact"
                                  >
                                    {moduleGroup.title}
                                  </Typography>
                                  <Typography
                                    className={styles.moduleLessonCount}
                                    variant="body3"
                                    mode="compact"
                                  >
                                    ({moduleGroup.lessons.length})
                                  </Typography>
                                </div>
                                {expanded ? (
                                  <div className={styles.moduleLessons}>
                                    {moduleGroup.lessons.map(renderLessonItem)}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })
                        : courseLessons.map(renderLessonItem)}
                    </div>
                  ) : (
                    <Typography className={styles.empty} variant="body2">
                      {getTranslation(
                        'courses_lessons_page.replace_drawer.lessons_empty',
                      )}
                    </Typography>
                  )}
                </>
              ) : null}
            </>
          ) : (
            <Typography className={styles.empty} variant="body2">
              {getTranslation('courses_lessons_page.replace_drawer.empty')}
            </Typography>
          )}
        </div>

        <div className={styles.actions}>
          <Button
            className={styles.action}
            size="md"
            variant="outlined"
            disabled={saving}
            onClick={onCancel}
          >
            {getTranslation('courses_lessons_page.replace_drawer.cancel')}
          </Button>
          <Button
            className={styles.action}
            size="md"
            disabled={!canSave || loading || lessonsLoading || saving}
            startIcon={
              saving ? <Loading color="var(--color-white-primary)" /> : undefined
            }
            onClick={onSave}
          >
            {getTranslation('courses_lessons_page.replace_drawer.save')}
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default React.memo(LessonReplaceInCourseDrawer) as any;
